import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { LANDING_DEFAULTS, SECTION_LABELS, SECTION_TITLE_KEYS, mergeLanding } from "@/lib/landingDefaults";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import WhatsAppSettingsCard from "@/components/WhatsAppSettingsCard";
import PortalSettingsCard from "@/components/PortalSettingsCard";
import QuickCmsEditor from "@/components/QuickCmsEditor";
import { ArrowUp, ArrowDown, ChevronDown, Plus, Trash2, Upload, Eye, Save, Send, Loader2 } from "lucide-react";

const human = (k) => k.replace(/_/g, " ").replace(/([a-z])([A-Z0-9])/g, "$1 $2").replace(/^./, (c) => c.toUpperCase());
const isLink = (v) => v && typeof v === "object" && !Array.isArray(v) && "url" in v && "label" in v;
const blank = (v) => (typeof v === "string" ? "" : typeof v === "number" ? 0 : typeof v === "boolean" ? true
  : Array.isArray(v) ? [] : v && typeof v === "object" ? Object.fromEntries(Object.entries(v).map(([k, x]) => [k, k === "id" ? `item-${Date.now()}` : blank(x)])) : v);

function ImageInput({ value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type) || f.size > 2 * 1024 * 1024) return toast.error("JPG, PNG or WebP up to 2 MB");
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/offers/image", fd); onChange(data.image_url); }
    catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2">
      {value && <img src={value} alt="" className="h-10 w-14 object-cover rounded" />}
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="flex-1" data-testid={testid} />
      <label className="h-9 px-3 inline-flex items-center gap-1 rounded-md text-xs cursor-pointer" style={{ border: "1px solid var(--border-strong)" }}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload
        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} />
      </label>
    </div>
  );
}

const Rec = {};

function ListEditor({ value, template, onChange, path }) {
  const move = (i, d) => { const n = [...value]; [n[i], n[i + d]] = [n[i + d], n[i]]; onChange(n); };
  return (
    <div className="space-y-2">
      {value.map((item, i) => (
        <div key={i} className="flex gap-2 items-start rounded-lg p-2" style={{ border: "1px solid var(--border)" }}>
          <div className="flex-1 min-w-0"><Rec.F k="" value={item} template={template} path={`${path}-${i}`}
                                                  onChange={(v) => onChange(value.map((x, j) => (j === i ? v : x)))} /></div>
          <div className="flex flex-col">
            <Button type="button" size="sm" variant="ghost" disabled={!i} onClick={() => move(i, -1)} aria-label="Up"><ArrowUp className="h-3.5 w-3.5" /></Button>
            <Button type="button" size="sm" variant="ghost" disabled={i === value.length - 1} onClick={() => move(i, 1)} aria-label="Down"><ArrowDown className="h-3.5 w-3.5" /></Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => onChange(value.filter((_, j) => j !== i))} aria-label="Delete"
                    data-testid={`cms-del-${path}-${i}`}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button>
          </div>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" className="rounded-full" data-testid={`cms-add-${path}`}
              onClick={() => onChange([...value, blank(template)])}><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
    </div>
  );
}

function Field({ k, value, template, onChange, path }) {
  const t = template ?? value;
  const label = k && <div className="text-xs font-semibold mb-1" style={{ color: "var(--text-muted)" }}>{human(k)}</div>;
  if (typeof t === "boolean") return <label className="flex items-center gap-2 text-sm"><Switch checked={!!value} onCheckedChange={onChange} data-testid={`cms-${path}`} />{human(k || "published")}</label>;
  if (typeof t === "number") return <div>{label}<Input type="number" value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))} data-testid={`cms-${path}`} /></div>;
  if (typeof t === "string") {
    if (/image|logo/i.test(k)) return <div>{label}<ImageInput value={value} onChange={onChange} testid={`cms-${path}`} /></div>;
    const long = (t || "").length > 70 || /text|desc/i.test(k);
    return <div>{label}{long ? <Textarea rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`cms-${path}`} />
      : <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`cms-${path}`} />}</div>;
  }
  if (Array.isArray(t)) return <div>{label}<Rec.L value={value || []} template={t[0] ?? ""} onChange={onChange} path={path} /></div>;
  if (isLink(t)) return (
    <div>{label}<div className="grid grid-cols-2 gap-2">
      <Input placeholder="Label" value={value?.label || ""} onChange={(e) => onChange({ ...value, label: e.target.value })} data-testid={`cms-${path}-label`} />
      <Input placeholder="URL, /page, #section or whatsapp" value={value?.url || ""} onChange={(e) => onChange({ ...value, url: e.target.value })} data-testid={`cms-${path}-url`} />
    </div></div>);
  if (t && typeof t === "object") return (
    <div className="space-y-2">{label}
      {Object.keys(t).filter((x) => x !== "id").map((sk) => (
        <Rec.F key={sk} k={sk} value={value?.[sk]} template={t[sk]} path={`${path}-${sk}`} onChange={(v) => onChange({ ...value, [sk]: v })} />
      ))}
    </div>);
  return null;
}

Rec.F = Field;
Rec.L = ListEditor;

function LinkedDealSelect({ value, onChange }) {
  const [deals, setDeals] = useState(null);
  useEffect(() => {
    const now = new Date().toISOString();
    api.get("/offers").then(({ data }) => setDeals(data.filter((o) => o.approval_status === "Approved" && o.active
      && (!o.expiry_date || o.expiry_date > now)))).catch(() => {});
  }, []);
  const missing = value && deals && !deals.some((d) => d.id === value);
  return (
    <div>
      <div className="text-xs font-semibold mb-1" style={{ color: "var(--text-muted)" }}>Linked Deal</div>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid="cms-featured-linked-deal"
              className="w-full h-10 rounded-md px-3 text-sm bg-transparent" style={{ border: "1px solid var(--border-strong)" }}>
        <option value="">None (use the manual fields below)</option>
        {missing && <option value={value}>Unavailable deal (not live)</option>}
        {(deals || []).map((d) => <option key={d.id} value={d.id}>{d.title}{d.partner_name ? ` · ${d.partner_name}` : ""}</option>)}
      </select>
      <p className="text-xs mt-1" style={{ color: missing ? "#b45309" : "var(--text-muted)" }} data-testid="cms-featured-linked-hint">
        {missing ? "The linked deal is no longer live, so the banner is using the manual fields."
          : "A linked deal supplies the partner name, offer title, discount and price. The image and subtitle below override the deal's when filled in."}
      </p>
    </div>
  );
}

// Title is edited in SectionTitleField, so drop it from the generic field list
const withoutTitle = (id) => {
  const k = SECTION_TITLE_KEYS[id];
  if (!k) return LANDING_DEFAULTS[id];
  const { [k]: _omit, ...rest } = LANDING_DEFAULTS[id];
  return rest;
};

function SectionTitleField({ id, data, onChange }) {
  const key = SECTION_TITLE_KEYS[id];
  const [busy, setBusy] = useState(false);
  if (!key) return null;
  const value = data?.[key] ?? "";
  const send = async (live) => {
    if (!value.trim()) return toast.error("Section title can't be empty");
    setBusy(true);
    try {
      await api.patch("/admin/landing/title", { section: id, title: value, live });
      toast.success(live ? "Title updated on the live landing page" : "Title saved to draft");
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg p-3" style={{ background: "var(--bg-soft)", border: "1px solid var(--border)" }} data-testid={`cms-title-box-${id}`}>
      <div className="text-xs font-semibold mb-1" style={{ color: "var(--text-muted)" }}>Section title</div>
      <div className="flex gap-2 flex-wrap">
        <Input value={value} maxLength={120} onChange={(e) => onChange({ ...data, [key]: e.target.value })} className="flex-1 min-w-[200px]"
               placeholder={LANDING_DEFAULTS[id]?.[key]} data-testid={`cms-section-title-${id}`} />
        <Button type="button" variant="outline" size="sm" className="h-9 rounded-lg" disabled={busy} onClick={() => send(false)}
                data-testid={`cms-title-save-${id}`}><Save className="h-3.5 w-3.5 mr-1" />Save</Button>
        <Button type="button" size="sm" className="h-9 rounded-lg bg-navy text-white" disabled={busy} onClick={() => send(true)}
                data-testid={`cms-title-update-${id}`}><Send className="h-3.5 w-3.5 mr-1" />Update live</Button>
      </div>
      <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>Save keeps it in the draft. Update live changes only this title on the public page straight away.</p>
    </div>
  );
}

function SectionCard({ id, title, data, onChange, open, setOpen, controls, onAddItem }) {
  return (
    <div className="w-card w-card-bordered" data-testid={`cms-section-${id}`}>
      <div className="flex items-center gap-2 p-3">
        <button type="button" className="flex-1 text-left font-semibold flex items-center gap-2" onClick={() => setOpen(open ? null : id)}
                data-testid={`cms-open-${id}`}>
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />{title}
        </button>
        {onAddItem && (id === "featured" || id === "deals") && (
          <Button type="button" size="sm" variant="outline" className="rounded-full h-8 mr-1" onClick={() => onAddItem(id)}
                  data-testid={`cms-add-item-${id}`}><Plus className="h-3.5 w-3.5 mr-1" />Add Item</Button>
        )}
        {controls}
      </div>
      {open && <div className="px-4 pb-4 space-y-3">
        <SectionTitleField id={id} data={data} onChange={onChange} />
        {id === "quick" && <WhatsAppSettingsCard />}
        {id === "featured" && <p className="text-xs" style={{ color: "var(--text-muted)" }}>An offer marked "Featured Partner" in the Offers tab takes over this banner. Without one, the linked deal or the fields below are used.</p>}
        {id === "featured" && <LinkedDealSelect value={data?.deal_id} onChange={(v) => onChange({ ...data, deal_id: v })} />}
        {id === "deals" && <p className="text-xs" style={{ color: "var(--text-muted)" }}>Deal cards come from the Offers tab (section "Exclusive Member Deals", shown on Landing Page/Both). Use Add Item to create one.</p>}
        {id === "quick"
          ? <>
              <Field k="" value={data} template={{ subtitle: "", button: "" }} path={id} onChange={onChange} />
              <QuickCmsEditor value={data} onChange={onChange} />
            </>
          : <Field k="" value={data} template={withoutTitle(id)} path={id} onChange={onChange} />}
      </div>}
    </div>
  );
}

export default function LandingCmsPanel({ onAddItem }) {
  const [c, setC] = useState(null);
  const [meta, setMeta] = useState({});
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/landing").then(({ data }) => { setC(mergeLanding(data.draft)); setMeta(data); }).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);
  if (!c) return null;
  const set = (id) => (v) => setC({ ...c, [id]: v });
  const move = (i, d) => { const o = [...c.order]; [o[i], o[i + d]] = [o[i + d], o[i]]; setC({ ...c, order: o }); };
  const save = async () => { setBusy(true); try { await api.put("/admin/landing/draft", { content: c }); toast.success("Draft saved"); await load(); } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); } };
  const publish = async () => {
    if (!window.confirm("Publish the saved draft to the live landing page?")) return;
    setBusy(true);
    try { await api.put("/admin/landing/draft", { content: c }); await api.post("/admin/landing/publish"); toast.success("Landing page published"); await load(); }
    catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="landing-cms">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold">Landing Page</h2>
          <p className="text-xs" style={{ color: "var(--text-muted)" }}>
            Draft saved {meta.draft_updated_at ? new Date(meta.draft_updated_at).toLocaleString() : "—"} · Published {meta.published_at ? `${new Date(meta.published_at).toLocaleString()} by ${meta.published_by}` : "never (showing defaults)"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-xl" disabled={busy} onClick={save} data-testid="cms-save-draft"><Save className="h-4 w-4 mr-1" />Save draft</Button>
          <Button variant="outline" className="rounded-xl" onClick={() => window.open("/?preview=draft", "_blank")} data-testid="cms-preview"><Eye className="h-4 w-4 mr-1" />Preview draft</Button>
          <Button className="rounded-xl bg-navy text-white" disabled={busy} onClick={publish} data-testid="cms-publish"><Send className="h-4 w-4 mr-1" />Publish</Button>
        </div>
      </div>
      <PortalSettingsCard portal={c.portal} onChange={(portal) => setC({ ...c, portal })} />
      <SectionCard id="header" title="Header" data={c.header} onChange={set("header")} open={open === "header"} setOpen={setOpen} />
      {c.order.map((id, i) => (
        <SectionCard key={id} id={id} title={`${i + 1}. ${SECTION_LABELS[id]}`} data={c[id]} onChange={set(id)} open={open === id} setOpen={setOpen} onAddItem={onAddItem}
          controls={<>
            <label className="flex items-center gap-1.5 text-xs mr-2"><Switch checked={!c.hidden?.[id]} data-testid={`cms-visible-${id}`}
              onCheckedChange={(v) => setC({ ...c, hidden: { ...c.hidden, [id]: !v } })} />{c.hidden?.[id] ? "Hidden" : "Shown"}</label>
            <Button type="button" size="sm" variant="ghost" disabled={!i} onClick={() => move(i, -1)} aria-label="Move up" data-testid={`cms-up-${id}`}><ArrowUp className="h-4 w-4" /></Button>
            <Button type="button" size="sm" variant="ghost" disabled={i === c.order.length - 1} onClick={() => move(i, 1)} aria-label="Move down" data-testid={`cms-down-${id}`}><ArrowDown className="h-4 w-4" /></Button>
          </>} />
      ))}
      <SectionCard id="footer" title="Footer" data={c.footer} onChange={set("footer")} open={open === "footer"} setOpen={setOpen} />
    </div>
  );
}
